<template>
  <editable-content
    :title="$t('portal.general.user_settings.user_profile.labels.card_title')"
    :iconText="
      $t('portal.general.user_settings.user_profile.labels.edit_icon_text')
    "
    :editable="editable"
    @on-click-edit="toggleEditable()"
  >
    <v-skeleton-loader v-if="loadingData" type="article"></v-skeleton-loader>
    <v-form v-else ref="form" v-model="valid">
      <v-row>
        <v-col class="text-columns" md="4">
          <editable-input
            :id="'idPortalNombre'"
            :editable="editable"
            :rules="rules"
            v-model="currentUserData.first_name"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_name'
              )
            "
          />
          <v-select
            v-if="editable"
            id="idPortalTipoDeDocumento"
            :items="identificationType"
            v-model="currentUserData.identification_type"
            :rules="rules"
            clearable
            filled
            dense
            color="tertiary"
          >
            <template v-slot:item="data">
              <v-list-item__title
                style="color: #2B2B2B; font-family: Lato, sans-serif; font-size: 13px;"
                :id="
                  data.item.text === 'DNI'
                    ? 'idPortalDNI'
                    : data.item.text === 'RUT'
                    ? 'idPortalRUT'
                    : data.item.text === 'CI'
                    ? 'idPortalCI'
                    : ''
                "
              >
                {{ data.item.text }}
              </v-list-item__title>
            </template>
          </v-select>
          <editable-input
            v-else
            :editable="editable"
            :rules="rules"
            v-model="currentUserData.identification_type"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_identification_type'
              )
            "
          />
          <editable-input
            v-for="phone in currentUserData.phones"
            :key="phone"
            :value="mapPhoneTagType(phone.tag)"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_phone_type'
              )
            "
          />
          <editable-input
            v-for="email in currentUserData.emails"
            :key="email"
            :value="mapEmailTagType(email.tag)"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_mail_type'
              )
            "
          />
          <editable-input
            v-for="(group, index) in getUserRoles()"
            :key="index"
            :value="group.displayName"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_user_rol'
              )
            "
          />
        </v-col>

        <v-col class="text-columns" md="4">
          <editable-input
            :id="'idPortalApellido'"
            :editable="editable"
            :rules="rules"
            v-model="currentUserData.last_name"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_surname'
              )
            "
          />
          <editable-input
            :id="'idPortalDocumento'"
            :editable="editable"
            :rules="rules"
            v-model="currentUserData.identification_number"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_identification_number'
              )
            "
          />
          <editable-input
            v-for="phone in currentUserData.phones"
            :key="phone"
            :rules="phoneRules"
            v-model="phone.number"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_phone_number'
              )
            "
          />
          <editable-input
            v-for="email in currentUserData.emails"
            :key="email"
            v-model="email.email"
            :label="
              $t(
                'portal.general.user_settings.user_profile.labels.card_field_mail'
              )
            "
          />
        </v-col>

        <v-col class="text-columns" md="4">
          <editable-image
            rounded
            width="200px"
            height="200px"
            :editable="editable"
            v-model="currentUserData.photo"
            :initialAvatarUrl="userImageUrl"
            @image-updated="updateProfileImage"
          />
        </v-col>
      </v-row>
      <v-row
        class="footer"
        :style="{ visibility: editable ? 'visible' : 'hidden' }"
      >
        <v-col>
          <v-row justify-md="end">
            <v-col cols="2">
              <v-btn
                id="idPortalCancelar"
                class="magenta-button mr-4"
                @click="cancel"
                outlined="true"
                color="secondary"
                rounded
                block
                >{{
                  $t(
                    'portal.general.user_settings.user_profile.labels.edit_cancel_button'
                  )
                }}</v-btn
              >
            </v-col>
            <v-col cols="2">
              <v-btn
                elevation="0"
                id="idPortalGuardar"
                class="white--text"
                @click="confirmUpdate()"
                color="deepSkyBlue"
                rounded
                block
                :disabled="!valid || !isUserDataChanged"
                >{{
                  $t(
                    'portal.general.user_settings.user_profile.labels.edit_save_button'
                  )
                }}</v-btn
              >
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar v-model="snack" :color="snackColor" :timeout="snackBarTimeout">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" text @click="snack = false">
          {{
            $t(
              'portal.general.user_settings.user_profile.labels.card_snackbar_close_button'
            )
          }}
        </v-btn>
      </template>
    </v-snackbar>
    <Dialog
      v-if="showDialog"
      @cancelButtonPressed="showDialog = false"
      :showDialog="showDialog"
      @submitButtonPressed="updatePerson"
      :loading="loading"
      :text="dialog.text"
      type="warning"
    />
  </editable-content>
</template>

<script>
import EditableContent from './EditableContent.vue'
import EditableInput from './EditableInput.vue'
import EditableImage from './EditableImage.vue'
import Dialog from '../dialog/Dialog'
import i18n from '@/plugins/i18n'
import { SNACKBAR_TIMEOUT, magentaOAuth } from '@leapsight/styleguide'
import PersonsHelpers from '../../helpers/persons/personsHelpers.js'
import store from '@/store'

import { mapGetters } from 'vuex'

export default {
  components: {
    EditableContent,
    EditableInput,
    EditableImage,
    Dialog
  },
  data() {
    return {
      editable: false,
      loading: false,
      snack: false,
      snackText: '',
      snackColor: '',
      snackBarTimeout: SNACKBAR_TIMEOUT,
      dialog: {
        text: '',
        type: '',
        loading: false
      },
      person: {},
      showDialog: false,
      personDuplicated: false,
      errorMessage: '',
      valid: true,
      rules: [
        v =>
          !!v ||
          i18n.t('portal.general.user_settings.user_profile.rules.required')
      ],
      phoneRules: [
        v =>
          !!v ||
          i18n.t('portal.general.user_settings.user_profile.rules.required'),
        v =>
          /^\+[\d]{1,3}[0-9]{10}$/.test(v) ||
          i18n.t('portal.general.user_settings.user_profile.rules.valid_phone')
      ],
      loadingData: false,
      currentUserData: {},
      userCopyData: {},
      identificationType: [
        {
          id: 1,
          text: 'DNI'
        },
        {
          id: 2,
          text: 'RUT'
        },
        {
          id: 3,
          text: 'CI'
        }
      ],
      prevImg: null,
      isImageUpdated: null
    }
  },
  computed: {
    ...mapGetters('auth', ['user', 'getResponseMessage', 'getResponseError']),
    userImageUrl() {
      return this.currentUserData.photo
    },
    sessionIsOpen() {
      return this.$store.getters['auth/connection']?.session?.isOpen
    },
    isUserDataChanged() {
      return (
        JSON.stringify(this.currentUserData) !==
          JSON.stringify(this.userCopyData) || this.isImageUpdated
      )
    },
    connection() {
      return this.$store.getters['auth/connection']
    }
  },
  methods: {
    prepareUserDataFromStore() {
      const user = this.$store.getters['auth/user']

      if (!user) return

      this.currentUserData = user
      this.userCopyData = JSON.parse(JSON.stringify(user))
      this.prevImg = user.photo
      this.editable = false

      if (user.photo) {
        localStorage.setItem('user_avatar', user.photo)
      } else {
        localStorage.removeItem('user_avatar')
      }
    },
    confirmUpdate() {
      this.showDialog = true
      this.dialog.text = i18n.t(
        'portal.general.user_settings.user_profile.alerts.confirm_update'
      )
      this.dialog.type = 'warning'
    },
    async updatePerson() {
      this.loading = true

      const uriId = localStorage.getItem('user_id')
      const firstName = this.currentUserData.first_name
      const lastName = this.currentUserData.last_name
      const identifyType = this.currentUserData.identification_type
      const identifyNumber = this.currentUserData.identification_number
      const photo = this.currentUserData.photo

      try {
        const updatedPhones = this.currentUserData.phones?.map(phone => ({
          tag: phone.tag,
          number: phone.number
        }))

        const res = await PersonsHelpers.updatePerson(uriId, {
          firstName,
          lastName,
          identifyType,
          identifyNumber,
          phones: updatedPhones,
          photo
        })
        const personUpdated = res.args[0]

        await this.$store.commit('auth/setUser', {
          uriId: personUpdated.id,
          firstName: personUpdated.first_name,
          lastName: personUpdated.last_name,
          identifyType: personUpdated.identification_type,
          identifyNumber: personUpdated.identification_number,
          phones: personUpdated.phones,
          photo: personUpdated.photo
        })

        localStorage.setItem('user_avatar', personUpdated.photo)

        this.$root.$emit('imageSelected', photo)

        this.updateUser({
          name: personUpdated.first_name,
          last_name: personUpdated.last_name
        })

        this.currentUserData = personUpdated

        this.snack = true
        this.snackColor = 'success'
        this.snackText = i18n.t(
          'portal.general.user_settings.user_profile.alerts.update_success'
        )
        this.loading = false
        this.showDialog = false
      } catch (error) {
        console.log(error)
        this.snack = true
        this.snackColor = 'error'
        this.snackText = i18n.t(
          'portal.general.user_settings.user_profile.errors.update_error'
        )
        this.loading = false
        this.showDialog = false
      } finally {
        this.toggleEditable()
      }
    },
    updateUser(user) {
      window.dispatchEvent(
        new CustomEvent('update-people-data', { detail: user })
      )
    },
    updateProfileImage({ avatar, hasImage }) {
      this.currentUserData.photo = avatar
      this.isImageUpdated = hasImage
    },
    cancel() {
      this.isImageUpdated = false
      this.currentUserData = JSON.parse(JSON.stringify(this.userCopyData))
      this.toggleEditable()
    },
    toggleEditable() {
      this.editable = !this.editable
    },
    getUserRoles() {
      if (
        this.currentUserData &&
        this.currentUserData.roles &&
        this.currentUserData.roles['mrn:party_role:user'] &&
        this.currentUserData.roles['mrn:party_role:user'].groups
      ) {
        return this.currentUserData.roles['mrn:party_role:user'].groups.map(
          group => {
            return { value: group, displayName: this.mapRolesUser(group) }
          }
        )
      } else {
        return []
      }
    },
    mapRolesUser(role) {
      switch (role) {
        case 'business_account_admin':
          return 'Administrador'
          break
        case 'business_account_user':
          return 'Monitoreo'
          break
        case 'admin':
          return 'Admin'
          break
        case 'emergency_contact':
          return 'Contacto de Emergencia'
          break
        default:
          return '-'
      }
    },
    mapPhoneTagType(tag) {
      switch (tag) {
        case 'mobile':
          return 'Móvil'
          break
        case 'work':
          return 'Trabajo'
          break
        case 'home':
          return 'Fijo'
          break
        case 'other':
          return 'Otro'
          break
        default:
          return '-'
      }
    },
    mapEmailTagType(tag) {
      switch (tag) {
        case 'personal':
          return 'Personal'
          break
        case 'work':
          return 'Trabajo'
          break
        default:
          return '-'
      }
    }
  },
  watch: {
    async sessionIsOpen(newValue) {
      this.loadingData = true
      if (newValue) {
        this.loadingData = false
      }
      this.currentUserData = { ...this.user }
    },
    currentUserData(newData) {
      this.userCopyData = { ...newData }
    }
  },
  async created() {
    this.loadingData = true
    if (store.getters['auth/isLoggedIn']) {
      try {
        const response = await store.dispatch('auth/checkTokenIsActive')
        if (response === true) {
          if (!store.getters['auth/wsConnectionActive']) {
            if (!this.connection?.session?.isOpen) {
              await store.dispatch('auth/establishConnection')
            }
          }
        } else if (response === false) {
          const refresh = await magentaOAuth.refreshToken()
          if (refresh.status === 200) {
            await store.dispatch('auth/establishConnection')
          }
        }
      } catch (e) {
        console.error(e)
      }
    }

    this.loadingData = false
    this.currentUserData = { ...this.user }
  },
  mounted() {
    this.prepareUserDataFromStore()
  }
}
</script>

<style lang="scss" scoped></style>
